import filter from 'lodash/filter';
import flatMap from 'lodash/flatMap';
import get from 'lodash/get';
import identity from 'lodash/identity';
import isEmpty from 'lodash/isEmpty';
import keyBy from 'lodash/keyBy';
import map from 'lodash/map';
import mapValues from 'lodash/mapValues';
import pickBy from 'lodash/pickBy';
import * as rules from '@appcraft/form-sdk/rules';
import { hasRequiredTransportData } from '../fields/CarbonFootprintTransportField/utils/carbonFootprint.utils';
import MultiNumericField from '../fields/MultiNumericField';
import { hasValidRoomingData } from '../fields/RoomingField/utils';
import { evalCondition } from '@appcraft/common-utils';
import { isFieldLocked } from '../components/utils';
import { evalIfNeeded, isExpression } from './sanitizeData';

// Field extraction and required-field rules are shared with the custom forms SDK and
// registration-service (@appcraft/form-sdk/rules). Field types whose check depends on the
// React front (current participant, components) plug their own validator here.
export { extractFields, extractCurrentFields, extractRequiredFields } from '@appcraft/form-sdk/rules';

const requiredRulesContext = {
  // A locked answer can't be changed: rules never block on it (multiple RadioField minCount)
  isLocked: (field) => isFieldLocked(field.name, field.locked),
  validators: {
    RoomingField: (field, data) => hasValidRoomingData(data[field.name], field.invitations, window.__DATA__?.data?._id),
    MultiNumericField: (field, data) => MultiNumericField.validate(field, data),
    CarbonFootprintTransportField: (_field, data) => hasRequiredTransportData(data),
  },
};

function cleanObject(obj) {
  if (!obj) return {};
  return pickBy(obj, identity); // Remove falsey values
}

/**
 * Tells whether a value is worth showing in the summary.
 * Unlike lodash `isEmpty`, booleans and numbers are values: a checked checkbox (`true`) or `0` count,
 * an unchecked checkbox (`false`) does not.
 */
export function hasSummaryValue(value) {
  if (value === undefined || value === null || value === '' || value === false) return false;
  if (typeof value === 'boolean' || typeof value === 'number') return true;
  return !isEmpty(value);
}

export function hasOriginalData(key) {
  return !isEmpty(get(window?.__DATA__?.data, key));
}

export function validateListItems(listItemField, items, parentData) {
  return rules.validateListItems(listItemField, items, parentData, requiredRulesContext);
}

export function hasRequiredData(fields, data, parentData) {
  return rules.hasRequiredData(fields, data, parentData, requiredRulesContext);
}

// Get the list of missing required fields with their names and labels
export function getMissingRequiredFields(fields, data, parentData) {
  return rules.getMissingRequiredFields(fields, data, parentData, requiredRulesContext);
}

// Form-wide constraint: minimum number of selected workshops across all
// WorkshopRegistrationV2 blocks. Set in the backoffice at `form.options.minWorkshopsCount`,
// exposed to the renderer as `window.__DATA__.options` (see computeFormOptions server-side).
// Returns the required minimum when unmet, otherwise null. Skipped if no block is visible.
export function getUnmetWorkshopsMinCount(fields, data) {
  const minCount = Number(window.__DATA__?.options?.minWorkshopsCount) || 0;
  if (!minCount) return null;

  const visibleWorkshopFields = rules.extractCurrentFields(fields, data).filter((f) => f.type === 'WorkshopRegistrationV2');
  if (visibleWorkshopFields.length === 0) return null; // no visible block -> skip

  // Plain array + includes on purpose: building a Set/record never pays off for the handful
  // of registrations we check here, and measures ~2x slower at realistic sizes (tens of sessions).
  const workshopsIds = flatMap(visibleWorkshopFields, (f) => map(f.workshops || [], '_id'));
  const selectedCount = filter(data.workshopsRegistrations || [], (r) => workshopsIds.includes(r.sessionId)).length;

  return selectedCount < minCount ? minCount : null;
}

export function initializeData(form, params) {
  const fields = rules.extractFields(form); //.map(f => f.name);
  const defaultData = (window.__DATA__ && window.__DATA__.data) || {};
  const defaultValues = keyBy(
    fields.filter((f) => f.hasOwnProperty('defaultValue') && !isExpression(f.defaultValue) && evalCondition(f.condition, defaultData)),
    'name',
  );
  const defaultParams = mapValues(defaultValues, (field) => evalIfNeeded(field.defaultValue, defaultData));
  if (!params) {
    params = {
      ...defaultParams,
      ...defaultData,
    };
  } else {
    params = {
      ...defaultParams,
      ...defaultData,
      ...cleanObject(params),
    };
  }
  // console.log("params", params)
  // const fieldNames = fields.map(f => f.name);
  return mapValues(params /*pick(params, fieldNames)*/, (v) => {
    if (v === 'true') return true;
    else if (v === 'false') return false;
    return v;
  });
}

export function hasProducts(fields) {
  // TODO : optimize (simple recursion and/or memoize ?)
  return !!rules.extractFields(fields).find((f) => f.type === 'ProductField');
}

export function isUpdate() {
  return window.__DATA__ && window.__DATA__.isUpdate;
}

export function isServiceV2() {
  return window.__DATA__?.api?.serviceVersion === 'v2';
}

export function getDefaultData() {
  return window.__DATA__?.data || {};
}
